import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { move_product_node_path, product_node_path } from '@/lib/routes'
import DisabledHint from './DisabledHint'
import { sendJson, type Group, type TreeProps } from './api'

interface Props {
  group: Group
  onTree: (tree: TreeProps) => void
  onDeleted: () => void
  onAddSub: () => void
}

export default function GroupToolbar({ group, onTree, onDeleted, onAddSub }: Props) {
  const [name, setName] = useState(group.name)
  const [errors, setErrors] = useState<string[]>([])

  useEffect(() => {
    setName(group.name)
    setErrors([])
  }, [group.id, group.name])

  async function run(method: 'PATCH' | 'DELETE', url: string, body?: unknown) {
    const res = await sendJson<TreeProps>(url, method, body)
    if (!res.ok) return setErrors(res.errors)
    setErrors([])
    if (method === 'DELETE') onDeleted()
    else if (res.data) onTree(res.data)
  }

  const cannotSave = !name.trim() || name === group.name
  const save = () => !cannotSave && run('PATCH', product_node_path(group.id), { name })
  const hasProducts = group.is_leaf && group.product_count > 0

  return (
    <div className="border-b border-border px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={name}
          className="h-8 w-56"
          aria-label="Group name"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
        />
        <DisabledHint reason={cannotSave ? 'Change the name first' : null}>
          <Button size="sm" className="h-8" disabled={cannotSave} onClick={save}>Save</Button>
        </DisabledHint>
        <DisabledHint reason={hasProducts ? 'Move its products out first' : null}>
          <Button size="sm" variant="outline" className="h-8" disabled={hasProducts} onClick={onAddSub}>
            <Plus size={14} className="mr-1" /> Add sub-group
          </Button>
        </DisabledHint>
        <Button size="sm" variant="outline" className="h-8" onClick={() => run('PATCH', move_product_node_path(group.id), { direction: 'up' })}>
          <ArrowUp size={14} className="mr-1" /> Move up
        </Button>
        <Button size="sm" variant="outline" className="h-8" onClick={() => run('PATCH', move_product_node_path(group.id), { direction: 'down' })}>
          <ArrowDown size={14} className="mr-1" /> Move down
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-destructive" onClick={() => run('DELETE', product_node_path(group.id))}>
          <Trash2 size={14} className="mr-1" /> Delete
        </Button>
      </div>
      {errors.map((e) => <p key={e} className="mt-2 text-sm text-destructive">{e}</p>)}
    </div>
  )
}
